/**
 * Reads the backend's side of a view-as session from API responses. The generated client predates the view-as
 * contract, so the `viewAs` echo on `GET /me` and `GET /permissions/my-permissions` and the `VIEW_AS_*` refusal
 * codes are narrowed here from unknown data instead of being trusted from the generated types.
 */
export type ViewAsMode = 'read-only' | 'full';

/** The backend's echo: who signed the request and whom it runs as. */
export interface ViewAsStatus {
  readonly realUser: string;
  readonly target: string;
  readonly mode: ViewAsMode;
}

export const VIEW_AS_MODES: ReadonlySet<unknown> = new Set<ViewAsMode>(['read-only', 'full']);

const VIEW_AS_CODE_PREFIX = 'VIEW_AS_';
const VIEW_AS_REFUSAL_STATUSES: ReadonlySet<unknown> = new Set([400, 403]);

export function isViewAsStatus (value: unknown): value is ViewAsStatus {
  return (
    typeof value === 'object' &&
    value !== null &&
    'realUser' in value &&
    typeof value.realUser === 'string' &&
    'target' in value &&
    typeof value.target === 'string' &&
    'mode' in value &&
    VIEW_AS_MODES.has(value.mode)
  );
}

/** Returns the `viewAs` echo of a `/me` or my-permissions body, or undefined when nobody is viewed as. */
export function readViewAsEcho (data: unknown): ViewAsStatus | undefined {
  if (typeof data !== 'object' || data === null || !('viewAs' in data)) {
    return undefined;
  }
  return isViewAsStatus(data.viewAs) ? data.viewAs : undefined;
}

/** Returns the HTTP status of an RTK Query error, or undefined for a network failure. */
export function getErrorStatus (error: unknown): number | undefined {
  return typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number'
    ? error.status
    : undefined;
}

/** Returns the `VIEW_AS_*` code of a 400 or 403 RTK Query error, or undefined for any other error. */
export function getViewAsRefusalCode (error: unknown): string | undefined {
  if (!VIEW_AS_REFUSAL_STATUSES.has(getErrorStatus(error)) || typeof error !== 'object' || error === null) {
    return undefined;
  }
  const data: unknown = 'data' in error ? error.data : undefined;
  if (typeof data !== 'object' || data === null || !('code' in data) || typeof data.code !== 'string') {
    return undefined;
  }
  return data.code.startsWith(VIEW_AS_CODE_PREFIX) ? data.code : undefined;
}
